import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import SEOHead from '../components/SEOHead';

const CGV = () => {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead 
        title="Conditions Générales de Vente - Golden Mali Suite"
        description="Conditions générales de vente de la plateforme Golden Mali Suite"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-navy-800 mb-6">
            Conditions Générales de Vente
          </h1>
          
          <div className="prose max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Dernière mise à jour :</strong> Février 2026
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">1. Objet</h2>
              <p>
                Les présentes Conditions Générales de Vente (CGV) régissent l'accès et l'utilisation 
                de la plateforme SaaS <strong>Golden Mali Suite</strong>, édité par <strong>GOLDEN MALI SARL</strong>.
              </p>
              <p>
                Toute utilisation de la plateforme implique l'acceptation sans réserve des présentes CGV.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">2. Informations Légales</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p><strong>Éditeur :</strong> GOLDEN MALI SARL</p>
                <p><strong>Forme juridique :</strong> Société à Responsabilité Limitée (SARL)</p>
                <p><strong>Gérant :</strong> FOFANA Ibrahima</p>
                <p><strong>Adresse du siège social :</strong> Imm DJOGUIBA, 2ème étage, bureau C4, Grand marché, Bamako, République du Mali</p>
                <p><strong>Téléphone :</strong> +223 20 22 58 56 / +223 66 92 25 45</p>
                <p><strong>NINA :</strong> 42009193276098T</p>
                <p><strong>RCCM :</strong> MA.BKO.2020.B.7764</p>
                <p><strong>Site web :</strong> https://golden-mali.cloud</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Développement et Maintenance :</strong> Créatif-web</p>
                <p><strong>Statut :</strong> Entrepreneur Individuel (EI)</p>
                <p><strong>Adresse :</strong> 16 rue Vendôme, 13500 Martigues, France</p>
                <p><strong>Téléphone :</strong> 06.10.56.25.06</p>
                <p><strong>Email :</strong> contact@creatifboost.com</p>
                <p><strong>Site web :</strong> https://creatif-web.eu</p>
                <p><strong>SIRET :</strong> 93466266900019</p>
                <p><strong>SIREN :</strong> 934662669</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">3. Description du Service</h2>
              <p>
                Golden Mali Suite est une plateforme SaaS de gestion de flux d'or permettant :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>La gestion des transactions d'achat et de vente d'or</li>
                <li>Le suivi des positions ouvertes</li>
                <li>La gestion du stock physique</li>
                <li>Le calcul automatique des marges et profits</li>
                <li>La gestion des paiements SWIFT internationaux</li>
                <li>La synchronisation automatique des données</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">4. Accès au Service</h2>
              <p>
                L'accès à la plateforme est réservé aux utilisateurs autorisés par GOLDEN MALI SARL. 
                Chaque utilisateur reçoit des identifiants personnels et confidentiels.
              </p>
              <p>
                L'utilisateur s'engage à :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Utiliser la plateforme conformément à sa destination</li>
                <li>Ne pas partager ses identifiants de connexion</li>
                <li>Respecter la confidentialité des données</li>
                <li>Informer immédiatement en cas de perte ou vol des identifiants</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">5. Disponibilité du Service</h2>
              <p>
                GOLDEN MALI SARL s'efforce d'assurer une disponibilité maximale de la plateforme. 
                Cependant, des interruptions peuvent survenir pour :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintenance programmée (avec préavis dans la mesure du possible)</li>
                <li>Mises à jour techniques</li>
                <li>Cas de force majeure</li>
              </ul>
              <p>
                GOLDEN MALI SARL ne saurait être tenue responsable des dommages résultant d'une 
                indisponibilité temporaire du service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">6. Responsabilité</h2>
              <p>
                La plateforme Golden Mali Suite facilite la gestion des transactions d'or et des 
                paiements SWIFT. GOLDEN MALI SARL n'intervient pas en tant qu'intermédiaire financier 
                et n'est pas responsable des transactions effectuées entre les utilisateurs et leurs partenaires.
              </p>
              <p>
                L'utilisation de la plateforme se fait sous la responsabilité exclusive de l'utilisateur. 
                GOLDEN MALI SARL ne saurait être tenue responsable :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Des dommages directs ou indirects résultant de l'utilisation de la plateforme</li>
                <li>Des pertes de données résultant d'une utilisation incorrecte</li>
                <li>Des décisions prises sur la base des informations fournies par la plateforme</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">7. Propriété Intellectuelle</h2>
              <p>
                L'ensemble des éléments de la plateforme (textes, images, logos, graphiques, logiciels) 
                sont la propriété exclusive de GOLDEN MALI SARL et sont protégés par les lois françaises 
                et internationales relatives à la propriété intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication ou adaptation de tout ou 
                partie des éléments de la plateforme est interdite sans autorisation écrite préalable 
                de GOLDEN MALI SARL.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">8. Modification des CGV</h2>
              <p>
                GOLDEN MALI SARL se réserve le droit de modifier les présentes CGV à tout moment. 
                Les utilisateurs sont invités à consulter régulièrement cette page pour prendre 
                connaissance des éventuelles modifications.
              </p>
              <p>
                L'utilisation continue de la plateforme après modification des CGV vaut acceptation 
                des nouvelles conditions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">9. Loi Applicable et Juridiction</h2>
              <p>
                Les présentes CGV sont régies par le droit français, conformément à la loi pour la 
                confiance en l'économie numérique (LCEN).
              </p>
              <p>
                En cas de litige, et à défaut d'accord amiable, le litige sera porté devant les 
                tribunaux français compétents, conformément aux règles de compétence en vigueur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">10. Contact</h2>
              <p>
                Pour toute question concernant les présentes CGV :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>GOLDEN MALI SARL</strong></p>
                <p>Imm DJOGUIBA, 2ème étage, bureau C4</p>
                <p>Grand marché, Bamako, République du Mali</p>
                <p><strong>Téléphone :</strong> +223 20 22 58 56 / +223 66 92 25 45</p>
                <p><strong>Email :</strong> support@golden-mali.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default CGV;
